/**
 * The client-side project folder tree.
 *
 * Stored as nested plain objects on `organization.projectFolders` (JSON), where
 * `{ Fiction: { Drafts: {} } }` means `/Fiction` and `/Fiction/Drafts`. Keys are
 * folder names; values are their children.
 *
 * **Why this class is defensive.** The tree is one of two independent sources of
 * truth -- the other being the `Project.folder` string on each Project -- and it
 * is the *only* record that an empty folder exists.
 * `ProjectsComponent.healOrganizationFolderStructure()` can rebuild folders from
 * `project.folder` values, so it recovers a folder only if that folder still
 * contains Projects. **A folder dropped out of this object while empty is gone
 * with nothing to reconcile it from.**
 *
 * Every mutator here therefore refuses rather than overwrites. Six silent
 * data-loss paths were found and fixed on 2026-08-27 (see
 * tests/ProjectFolders.smoke.ts, which asserts each of them):
 *
 *   - `addFolder` on a name that already existed assigned `{}` over it,
 *     discarding the entire subtree. Creating something must never destroy.
 *   - `addFolder` accepted a name containing `/`, producing a key that
 *     `getFlattened()` reports as a path but `findFolderObject()` can never
 *     resolve, because lookup splits on `/`. The folder was created
 *     permanently unreachable.
 *   - `addFolder` accepted an empty name.
 *   - `renameFolder` onto an existing sibling overwrote it and its subtree.
 *   - `moveFolder` into its own descendant pointed the subtree at itself and
 *     then deleted it from its parent: a reference cycle AND the whole branch
 *     vanishing from `getFlattened()`, with no error raised.
 *   - `moveFolder` onto an existing folder of the same name overwrote it.
 *
 * The server keeps a second copy of this class
 * (SnowfallPressServerless/src/entity/supporting/ProjectFolders.ts). The two are
 * expected to agree; tests/ProjectFolders.smoke.ts is the reconciliation table.
 */
export class ProjectFolders {
  private folders: any;

  constructor(obj: any) {
    // `Organization.getProjectFolders()` falls back to `new ProjectFolders({})`
    // when the stored JSON is unusable, but it can only do that if constructing
    // from a bad value does not take the caller down first. A null tree is an
    // empty tree.
    const source = (obj && typeof obj === 'object') ? obj : {};
    this.validateFolderStructure(source);
    this.folders = source;
  }

  // --- Core Path Logic ---

  private findFolderObject(folderPath: string): any {
    if (folderPath === "/") {
      return this.folders;
    }

    const pathSegments = folderPath.split("/").filter((s) => {
      return s !== "";
    });
    let currentObj = this.folders;

    for (const segment of pathSegments) {
      currentObj = currentObj[segment];
      if (!currentObj) {
        return null;
      }
    }
    return currentObj;
  }

  private joinPath(parent: string, child: string): string {
    return parent === "/" ? `/${child}` : `${parent}/${child}`;
  }

  /**
   * Is `candidate` the same object as `root`, or anywhere inside it?
   *
   * Deliberately an identity walk over the object graph rather than a string
   * comparison of paths. The move guard needs to know whether the destination
   * lives inside the thing being moved, and the object graph is the actual
   * subject -- comparing paths would mean re-deriving and re-normalising them,
   * and would miss aliasing entirely.
   */
  private containsObject(root: any, candidate: any): boolean {
    if (root === candidate) {
      return true;
    }
    if (!root || typeof root !== 'object') {
      return false;
    }
    for (const key of Object.keys(root)) {
      if (this.containsObject(root[key], candidate)) {
        return true;
      }
    }
    return false;
  }

  // --- Public API ---

  getFlattened(): string[] {
    const flattened: string[] = [];
    this.flattenFolders(this.folders, "/", flattened, true);
    return flattened;
  }

  private flattenFolders(obj: any, parentPath: string, result: string[], allDecendants: boolean): void {
    Object.keys(obj).forEach((folderName) => {
      const currentPath = this.joinPath(parentPath, folderName);
      result.push(currentPath);
      if (allDecendants) {
        this.flattenFolders(obj[folderName], currentPath, result, allDecendants);
      }
    });
  }

  getChildFolders(parentFolder: string): string[] {
    const childFolders: string[] = [];
    const parentObj = this.findFolderObject(parentFolder);
    if (parentObj) {
      this.flattenFolders(parentObj, parentFolder, childFolders, false);
    }
    return childFolders;
  }

  /**
   * Ensure `newFolderName` exists under `parentFolder`.
   *
   * Idempotent: if the folder is already there this is a no-op, and in
   * particular does NOT reset it to empty.
   * `healOrganizationFolderStructure()` calls this in a loop for folders it
   * believes are missing, so "already exists" is an ordinary outcome rather
   * than an error -- but it must never be a destructive one.
   */
  addFolder(parentFolder: string, newFolderName: string): void {
    this.assertValidFolderName(newFolderName);
    const parentObj = this.findFolderObject(parentFolder);
    if (!parentObj) {
      throw new Error(`Parent folder ${parentFolder} not found.`);
    }
    if (Object.prototype.hasOwnProperty.call(parentObj, newFolderName)) {
      return; // already present -- leave it and its descendants alone
    }
    parentObj[newFolderName] = {};
  }

  renameFolder(parentFolderPath: string, oldName: string, newName: string): void {
    this.assertValidFolderName(newName);
    const parentObj = this.findFolderObject(parentFolderPath);
    if (!parentObj || !parentObj[oldName]) {
      throw new Error("Folder not found.");
    }
    if (newName === oldName) {
      return;
    }
    // Refuse rather than merge. Renaming A to B where B exists previously
    // discarded B and everything under it.
    if (Object.prototype.hasOwnProperty.call(parentObj, newName)) {
      throw new Error(`A folder named ${newName} already exists in ${parentFolderPath}.`);
    }
    parentObj[newName] = parentObj[oldName];
    delete parentObj[oldName];
  }

  /**
   * Move the folder at `sourceFolderPath` to live under `destinationParentPath`
   * as `folderName`.
   *
   * `folderName` is the name it takes at the destination, so this doubles as a
   * move-and-rename. The name it is removed under is derived from
   * `sourceFolderPath` rather than taken from `folderName`, so a caller passing
   * a mismatched pair can no longer delete the wrong sibling.
   */
  moveFolder(sourceFolderPath: string, destinationParentPath: string, folderName: string): void {
    this.assertValidFolderName(folderName);

    const sourceObj = this.findFolderObject(sourceFolderPath);
    const sourceSegments = sourceFolderPath.split("/").filter((s) => s !== "");
    const sourceName = sourceSegments[sourceSegments.length - 1];
    const sourceParentPath = "/" + sourceSegments.slice(0, -1).join("/");
    const sourceParentObj = this.findFolderObject(sourceParentPath);
    const destParentObj = this.findFolderObject(destinationParentPath);

    if (!sourceObj || !sourceName || !sourceParentObj || !destParentObj) {
      throw new Error("Source or destination not found.");
    }

    // Moving a folder into itself, or into one of its own descendants, used to
    // create a cycle and then unlink the branch from its parent -- the entire
    // subtree disappeared from getFlattened() with no error. There is no
    // sensible interpretation of the request, so refuse it.
    if (this.containsObject(sourceObj, destParentObj)) {
      throw new Error("Cannot move a folder into itself or one of its own subfolders.");
    }

    // Refuse rather than overwrite. This previously replaced the destination
    // folder and discarded everything under it.
    const movingWithinSameParent = sourceParentObj === destParentObj;
    const collides = Object.prototype.hasOwnProperty.call(destParentObj, folderName)
      && !(movingWithinSameParent && folderName === sourceName);
    if (collides) {
      throw new Error(`A folder named ${folderName} already exists in ${destinationParentPath}.`);
    }

    destParentObj[folderName] = sourceObj;
    delete sourceParentObj[sourceName];
  }

  deleteFolder(folderPath: string): void {
    if (folderPath === "/") {
      throw new Error("Cannot delete root.");
    }

    const pathSegments = folderPath.split("/").filter((s) => {
      return s !== "";
    });

    const folderName = pathSegments.pop();
    // Reconstruct the parent path from remaining segments
    const parentPath = "/" + pathSegments.join("/");

    if (!folderName) {
      throw new Error("Invalid folder path.");
    }

    const parentObj = this.findFolderObject(parentPath);
    if (parentObj && Object.prototype.hasOwnProperty.call(parentObj, folderName)) {
      // NOTE: this removes descendants with it. That is intended -- the caller
      // (DeleteProjectFolderComponent) has already established that no Project
      // sits at or under this path -- but it does mean empty subfolders go too,
      // and they are not recoverable by heal. Worth revisiting if operators
      // report losing structure.
      delete parentObj[folderName];
    } else {
      throw new Error("Folder not found.");
    }
  }

  getJSON(): any {
    return this.folders;
  }

  // --- Validation ---

  private validateFolderStructure(obj: any, parentPath: string = "/"): void {
    Object.keys(obj).forEach((folderName) => {
      if (!this.isValidFolderName(folderName)) {
        throw new Error(`Invalid folder name: ${this.joinPath(parentPath, folderName)}`);
      }
      this.validateFolderStructure(obj[folderName], this.joinPath(parentPath, folderName));
    });
  }

  /**
   * Validation was previously applied only in the constructor, so a bad name
   * could still be introduced through `addFolder` / `renameFolder` / `moveFolder`
   * and would then fail validation the *next* time the tree was loaded from
   * JSON -- turning the whole organization's folder tree into a throw, long
   * after the call that caused it.
   */
  private assertValidFolderName(folderName: string): void {
    if (!this.isValidFolderName(folderName)) {
      throw new Error(`Invalid folder name: ${folderName}`);
    }
  }

  private isValidFolderName(folderName: string): boolean {
    if (typeof folderName !== 'string' || folderName.length === 0) {
      return false;
    }
    // A '/' would make the folder unreachable: getFlattened() would report it
    // as a path, but findFolderObject() splits on '/' and could never resolve
    // it again.
    if (folderName.includes("/")) {
      return false;
    }
    return true;
  }
}

